import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Projectile } from '../entities/projectile';
import { WA_PHYSICS } from '../core/constants';
import { computeSafeSpawnPosition } from './spawnHelper';

/**
 * Grub (GunBound mobile, Shot 2): fires four bouncy spheres in an evenly
 * spaced fan. Each ball uses grenade-style bounce/fuse physics but a round
 * body, so they spread out, bounce and roll before exploding — splitting a
 * single shot into four equally spaced hits.
 */
export const WeaponGrub: WeaponDefinition = {
  id: 'grub',
  name: 'Grub',
  icon: 'grub.png',
  category: 'thrown',
  maxAmmo: 4,
  maxUsesPerTurn: 1,
  powerCharging: true,
  timerAdjustable: true,
  timerRange: [1, 5],
  showAimGuide: true,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, power, timer, world } = ctx;

    const maxSpeed = 750;
    const speed = (power / 100) * maxSpeed;

    const { COUNT, SPREAD_STEP_DEG } = WA_PHYSICS.GRUB;
    // Symmetric, equally spaced offsets around the aim angle. For 4 balls and
    // a 5-degree step this is [-7.5, -2.5, +2.5, +7.5] degrees.
    const center = (COUNT - 1) / 2;

    const balls: Projectile[] = [];
    for (let i = 0; i < COUNT; i++) {
      const offsetDeg = (i - center) * SPREAD_STEP_DEG;
      const rad = ((angle + offsetDeg) * Math.PI) / 180;
      const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

      const spawn = computeSafeSpawnPosition(worm, aimRad, 20, world);

      balls.push(
        new Projectile({
          x: spawn.x,
          y: spawn.y,
          vx: Math.cos(aimRad) * speed,
          vy: -Math.sin(aimRad) * speed,
          type: 'grub',
          timer,
          bounceH: WA_PHYSICS.GRUB.BOUNCE_HORIZONTAL,
          bounceV: WA_PHYSICS.GRUB.BOUNCE_VERTICAL,
          friction: WA_PHYSICS.GRUB.FRICTION,
          windAffected: false,
          explosionRadius: WA_PHYSICS.GRUB.EXPLOSION_RADIUS,
          maxDamage: WA_PHYSICS.GRUB.MAX_DAMAGE,
          ownerId: worm.id,
        }),
      );
    }

    return {
      success: true,
      entities: balls,
    };
  },
};
